import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function AdminXLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/adminX/login", { username, password });
      setUser(data);
      toast.success("Welcome back, Chris");
      navigate("/adminX", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setError(detail);
      if (status === 429) {
        setLocked(true);
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="adminX-login-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-zinc-100 p-8 md:p-10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.1)]">
        <div className="w-11 h-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-5">
          <Lock className="w-5 h-5" />
        </div>
        <h1 className="font-display font-extrabold text-2xl tracking-tighter text-zinc-900">Restricted access</h1>
        <p className="mt-2 text-sm text-zinc-500">Sign in with your admin credentials.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-800">Username</Label>
            <Input
              data-testid="adminX-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">Password</Label>
            <Input
              data-testid="adminX-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11"
            />
          </div>
          <Button
            data-testid="adminX-submit"
            type="submit"
            disabled={submitting || locked}
            className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12 text-base font-semibold disabled:opacity-60"
          >
            {locked ? "Locked" : submitting ? "Signing in…" : "Sign in"}
          </Button>
          {error && (
            <div data-testid="adminX-error" className={`text-sm rounded-xl px-3 py-2 ${locked ? "bg-red-50 text-red-800 border border-red-100" : "text-zinc-600"}`}>
              {error}
            </div>
          )}
          <div className="text-xs text-zinc-400 text-center">Access is limited to 3 attempts before a 15-minute lockout.</div>
        </form>
      </div>
    </main>
  );
}
